import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchEntityMetadata as fetchEntityMetadataService } from '../services/entityMetadata.service';
import type { EntityMetadataResponse } from '../services/types';
import type { RootState } from '../store';

// Async thunks for fetching entity metadata
export const fetchSitMetadata = createAsyncThunk(
  'entityMetadata/fetchSitMetadata',
  async ({
    entityId,
    tenantIds,
  }: {
    entityId: string;
    tenantIds: string[];
  }) => {
    const response = await fetchEntityMetadataService(entityId, 'sit', tenantIds);
    return { entityId, data: response };
  }
);

export const fetchLabelMetadata = createAsyncThunk(
  'entityMetadata/fetchLabelMetadata',
  async ({
    entityId,
    tenantIds,
  }: {
    entityId: string;
    tenantIds: string[];
  }) => {
    const response = await fetchEntityMetadataService(entityId, 'label', tenantIds);
    return { entityId, data: response };
  }
);

export const fetchPolicyMetadata = createAsyncThunk(
  'entityMetadata/fetchPolicyMetadata',
  async ({
    entityId,
    tenantIds,
  }: {
    entityId: string;
    tenantIds: string[];
  }) => {
    const response = await fetchEntityMetadataService(entityId, 'policy', tenantIds);
    return { entityId, data: response };
  }
);

// State types
type EntityMetadataItem = {
  loading: boolean;
  error: string | null;
  data: EntityMetadataResponse | null;
};

type EntityMetadataState = {
  sits: Record<string, EntityMetadataItem>;
  labels: Record<string, EntityMetadataItem>;
  policies: Record<string, EntityMetadataItem>;
};

const initialState: EntityMetadataState = {
  sits: {},
  labels: {},
  policies: {},
};

const entityMetadataSlice = createSlice({
  name: 'entityMetadata',
  initialState,
  reducers: {
    // Clear specific entity type metadata
    clearSitsMetadata: (state) => {
      state.sits = {};
    },
    clearLabelsMetadata: (state) => {
      state.labels = {};
    },
    clearPoliciesMetadata: (state) => {
      state.policies = {};
    },
    // Clear all entity metadata
    clearAllEntityMetadata: (state) => {
      state.sits = {};
      state.labels = {};
      state.policies = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // SIT metadata
      .addCase(fetchSitMetadata.pending, (state, action) => {
        const entityId = action.meta.arg.entityId;
        state.sits[entityId] = {
          loading: true,
          error: null,
          data: null,
        };
      })
      .addCase(fetchSitMetadata.fulfilled, (state, action) => {
        const entityId = action.payload.entityId;
        state.sits[entityId] = {
          loading: false,
          error: null,
          data: action.payload.data,
        };
      })
      .addCase(fetchSitMetadata.rejected, (state, action) => {
        const entityId = action.meta.arg.entityId;
        state.sits[entityId] = {
          loading: false,
          error: action.error.message || 'Failed to fetch SIT metadata',
          data: null,
        };
      })
      // Label metadata
      .addCase(fetchLabelMetadata.pending, (state, action) => {
        const entityId = action.meta.arg.entityId;
        state.labels[entityId] = {
          loading: true,
          error: null,
          data: null,
        };
      })
      .addCase(fetchLabelMetadata.fulfilled, (state, action) => {
        const entityId = action.payload.entityId;
        state.labels[entityId] = {
          loading: false,
          error: null,
          data: action.payload.data,
        };
      })
      .addCase(fetchLabelMetadata.rejected, (state, action) => {
        const entityId = action.meta.arg.entityId;
        state.labels[entityId] = {
          loading: false,
          error: action.error.message || 'Failed to fetch label metadata',
          data: null,
        };
      })
      // Policy metadata
      .addCase(fetchPolicyMetadata.pending, (state, action) => {
        const entityId = action.meta.arg.entityId;
        state.policies[entityId] = {
          loading: true,
          error: null,
          data: null,
        };
      })
      .addCase(fetchPolicyMetadata.fulfilled, (state, action) => {
        const entityId = action.payload.entityId;
        state.policies[entityId] = {
          loading: false,
          error: null,
          data: action.payload.data,
        };
      })
      .addCase(fetchPolicyMetadata.rejected, (state, action) => {
        const entityId = action.meta.arg.entityId;
        state.policies[entityId] = {
          loading: false,
          error: action.error.message || 'Failed to fetch policy metadata',
          data: null,
        };
      });
  },
});

// Selectors
export const selectSitsMetadata = (state: RootState) => state.entityMetadata.sits;
export const selectLabelsMetadata = (state: RootState) => state.entityMetadata.labels;
export const selectPoliciesMetadata = (state: RootState) => state.entityMetadata.policies;

export const { 
  clearSitsMetadata, 
  clearLabelsMetadata, 
  clearPoliciesMetadata, 
  clearAllEntityMetadata 
} = entityMetadataSlice.actions;
export default entityMetadataSlice.reducer;